import useTranslationModules from "@/i18n/useTranslationModules";
import { useTypedSelector } from "@/store/reducers/use-typed-selector";
import { useEventNameParam } from "@/utils/use-event-name-param";
import { useEventUuidParam } from "@/utils/use-event-uuid-param";
import { isDefined } from "@/utils/utils";
import { PropsWithChildren, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLanguageParam, getDefaultLanguage, eventPath } from "../utils";
import useRedirectLanguage from "./use-redirect-language";
import { Redirects } from "@/utils/redirects";

type MarketingRegisteredRoutesGateProps = {
	hasAccess: boolean;
	/** the page to send the user back to once they register - see returnToState in live-event */
	returnTo?: { referrer?: string };
};

// Note: none of these hooks should run before a user is registered
const MarketingRegisteredRoutesGate = ({ children, hasAccess, returnTo }: PropsWithChildren<MarketingRegisteredRoutesGateProps>) => {

	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);

	const navigate = useNavigate();
	const uuid = useEventUuidParam();
	const eventName = useEventNameParam();
	const language = useLanguageParam();

	useRedirectLanguage({ event: eventBundle?.event });

	const homepageToggleExists = isDefined<boolean>(eventBundle?.settings?.display_homepage);
	const homepageToggledOff = !eventBundle?.settings?.display_homepage;
	const hasHomepage = !!eventBundle?.homepage && !(homepageToggleExists && homepageToggledOff);
	const notValidTranslation = hasHomepage
		? !eventBundle?.homepage?.languages.includes(language)
		: !eventBundle?.sessions?.[0]?.languages.includes(language);
	const defaultLanguage = eventBundle ? getDefaultLanguage(eventBundle) : 'en';

	// Use the URL language param as the source of truth. Only fall back to the default
	// when the URL language is not a valid translation for this page.
	useTranslationModules({
		language: notValidTranslation
			? defaultLanguage
			: language
	});

	useEffect(() => {
		if (!hasAccess) return;
		// if its a single session, they are going to profile page
		// if homepage is toggled off, direct them back to the session page (which will live at base event path)
		if (notValidTranslation && (eventName || uuid) && eventBundle) {
			const baseLang = getDefaultLanguage(eventBundle);
			hasHomepage
				? navigate(`/${eventPath(eventName, uuid)}/${baseLang}/home`, { replace: true })
				: navigate(`/${eventPath(eventName, uuid)}/${baseLang}`, { replace: true });
		}
	}, [
		eventName,
		uuid,
		notValidTranslation,
		hasHomepage,
		eventBundle,
		navigate,
		hasAccess
	]);

	if (!hasAccess) {
		// replace, not push - otherwise the back button returns here and bounces straight out again
		return <Navigate to={Redirects.toRegistration(language)} state={returnTo} replace />;
	}
	return children;
};

export default MarketingRegisteredRoutesGate;